import { InfoWindow } from '@react-google-maps/api'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { app } from '../../../firebase'
import DateTime from '../../DateTime'
import { usePermissions } from '../../../hooks'
import { Trash } from 'lucide-react'
import { PinAttachmentPreview } from './PinAttachmentPreview'
import { deleteAttachmentFromStorage } from '../../../hooks/firebaseService'
import { deletePinMessageFromChat } from '../../../services/chatService'

const CustomPinInfoWindow = ({
  pin,
  anchor,
  onClose,
  tracks,
  setTrackingPins,
  currentUserId,
  socket
}) => {
  const editPermission = usePermissions('track', 'update')
  const deletePermission = usePermissions('pin', 'delete')
  const isCreator = pin?.user_id === currentUserId

  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)

  /** ------------ FORM ------------ */
  const [form, setForm] = useState({
    name: '',
    description: '',
    color: ''
  })

  /** ------------ IMAGES ------------ */
  const [existingImages, setExistingImages] = useState([])
  const [newImages, setNewImages] = useState([])
  const [attachmentsToDelete, setAttachmentsToDelete] = useState([])

  /** ------------ INIT ------------ */
  useEffect(() => {
    if (!pin) return

    resetStateFromPin()
  }, [pin])

  const resetStateFromPin = () => {
    setForm({
      name: pin.label || '',
      description: pin.description || '',
      color: pin.color
    })

    setExistingImages(pin.images || [])
    setNewImages([])
    setAttachmentsToDelete([])
  }

  if (!pin || !anchor) return null

  /** ------------ IMAGE HANDLERS ------------ */

  const handleAddImages = (e) => {
    const files = Array.from(e.target.files)
    if (!files.length) return

    const mapped = files.map((file, idx) => ({
      id: `new-${Date.now()}-${idx}`, // 🔑 stable key
      file,
      preview: URL.createObjectURL(file)
    }))

    setNewImages((prev) => [...prev, ...mapped])
  }

  const removeExistingImage = (id) => {
    setExistingImages((prev) => prev.filter((img) => img.id !== id))
    setAttachmentsToDelete((prev) => (prev.includes(id) ? prev : [...prev, id]))
  }

  const removeNewImage = (id) => {
    setNewImages((prev) => prev.filter((img) => img.id !== id))
  }

  /** ------------ FIREBASE ------------ */

  const uploadNewImages = async () => {
    if (!newImages.length) return []

    const storage = getStorage(app)

    return Promise.all(
      newImages.map(async ({ file }) => {
        const storageRef = ref(
          storage,
          `${process.env.REACT_APP_ENV === 'production' ? 'prod' : 'stage'
          }tracks/${pin.track_id}/${Date.now()}_${file.name}`
        )

        const snap = await uploadBytes(storageRef, file)
        const url = await getDownloadURL(snap.ref)

        return {
          latitude: pin.position.lat,
          longitude: pin.position.lng,
          photo_url: url
        }
      })
    )
  }

  /** ------------ UPDATE ------------ */

  const handleUpdate = async () => {
    try {
      setLoading(true)

      const newAttachments = await uploadNewImages()

      const payload = {
        name: form.name,
        description: form.description,
        color: form.color,
        newAttachments,
        attachmentsToDelete
      }

      const res = await tracks.updatePin(pin.id, payload)

      setTrackingPins((prev) => ({
        ...prev,
        customPins: prev.customPins.map((p) =>
          p.id === pin.id
            ? {
              ...p,
              label: form.name,
              description: form.description,
              images: res.data?.attachments || []
            }
            : p
        )
      }))

      if (socket && pin.incident_id) {
        socket.emit('update_pin_track', {
          incidentId: pin.incident_id,
          userId: currentUserId,
          pin: {
            id: pin.id,
            name: form.name,
            description: form.description,
            latitude: pin.position.lat,
            longitude: pin.position.lng,
            attachments: res.data?.attachments || []
          }
        })
      }

      setIsEditing(false)
      setLoading(false)
      toast.success('Pin updated successfully')
    } catch (err) {
      console.error(err)
      toast.error('Failed to update pin')
    } finally {
      setLoading(false)
    }
  }
 
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this pin?')) return
 
    try {
      setLoading(true)
      await tracks.deletePin(pin.id)
 
      setTrackingPins((prev) => ({
        ...prev,
        customPins: prev.customPins.filter((p) => p.id !== pin.id)
      }))
 
      if (socket && pin.incident_id) {
        socket.emit('remove_pin_track', {
          incidentId: pin.incident_id,
          userId: currentUserId,
          pin: { id: pin.id }
        })
      }
 

      // NEW: Sync with chat - delete associated messages
      if (pin.incident_id) {
        try {
          await deletePinMessageFromChat(pin.incident_id, pin.id)
        } catch (chatErr) {
          console.error('Failed to delete pin messages from chat:', chatErr)
        }
      }

      toast.success('Pin deleted successfully')
      onClose()
    } catch (err) {
      console.error(err)
      toast.error('Failed to delete pin')
    } finally {
      setLoading(false)
    }
  }




  /** ------------ UI ------------ */

  return (
    <InfoWindow
      key={isEditing ? 'edit' : 'view'}
      anchor={anchor}
      onCloseClick={onClose}
      options={{
        maxWidth: 500,
        pixelOffset: new window.google.maps.Size(0, -40),
        autoPanPadding: { top: 180, bottom: 50, left: 50, right: 50 }
      }}>
      <>
        <div className='text-lg font-semibold text-gray-900 pb-2 border-b'>
          Pin Point
        </div>
        <div className='w-full max-w-[500px] p-4 bg-white rounded-lg shadow-lg'>
          {/* HEADER */}
          <div className='flex items-center justify-between mb-3'>
            {isEditing ? (
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className='text-lg font-semibold text-gray-800 border border-gray-300 rounded px-2 py-1'
                maxLength={10}
              />
            ) : (
              <h3 className='text-lg !font-semibold !text-black'>
                {pin.label}
              </h3>
            )}

            <div className='flex gap-2'>
              {(isCreator || editPermission?.granted) &&
                (isEditing ? (
                  <>
                    <button
                      onClick={handleUpdate}
                      className='text-green-500 hover:text-green-700 cursor-pointer'
                      title='Save changes'
                      disabled={loading}>
                      <i
                        className={`fa ${loading ? 'fa-spinner fa-spin' : 'fa-check'
                          }`}></i>
                    </button>

                    <button
                      onClick={() => {
                        setIsEditing(false)
                        resetStateFromPin()
                      }}
                      className='text-gray-500 hover:text-gray-700 cursor-pointer'
                      title='Cancel'
                      disabled={loading}>
                      <i className='fa fa-times'></i>
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className='text-[#485fc7] cursor-pointer'
                    title='Edit pin'>
                    <i className='fa-regular fa-pen-to-square'></i>
                  </button>
                ))}

              {deletePermission?.granted && (
                <button
                  onClick={handleDelete}
                  className='text-red-500 hover:text-red-700 cursor-pointer'
                  title='Delete pin'
                  disabled={loading}>
                  <Trash size={18} />
                </button>
              )}
            </div>
          </div>

          {/* DESCRIPTION */}
          {isEditing ? (
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              className='w-full border p-2 rounded'
            />
          ) : (
            <p className='text-sm !font-semibold !text-black mb-1'>
              {pin.description}
            </p>
          )}

          {/* META */}
          <p className='text-xs text-gray-500'>
            <DateTime value={pin.timestamp} />
          </p>

          {/* IMAGES */}
          <div className='flex flex-wrap gap-2 mt-3'>
            {existingImages.map((img) => (
              <div key={`existing-${img.id}`} className='w-16'>
                <PinAttachmentPreview
                  attachment={img}
                  isEditing={isEditing}
                  onRemove={removeExistingImage}
                />
              </div>
            ))}

            {newImages.map((img) => (
              <div key={img.id} className='w-16'>
                <PinAttachmentPreview
                  attachment={{ ...img, url: img.preview, fileType: img.file?.type }}
                  isEditing={isEditing}
                  onRemove={removeNewImage}
                />
              </div>
            ))}
          </div>

          {isEditing && (
            <label className='text-blue-600 text-sm cursor-pointer mt-2 inline-block'>
              + Add Images
              <input type='file' multiple hidden onChange={handleAddImages} />
            </label>
          )}
        </div>
      </>
    </InfoWindow>
  )
}

export default CustomPinInfoWindow
